import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMissatgeError } from '@/api/errors';
import {
  nomUsuari,
  useArees,
  useCircuitsServeis,
  useDesaExperts,
  useExpertsServeis,
  useNoms,
  useServeis,
  useUsuaris,
  type ExpertServeiDto,
  type ServeiDto,
} from '@/api/planificacio';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Avatar, BadgeArea, Modal } from '@/pages/operacio/comu';
import { Permis } from '@/permisos/Permis';
import './planificacio.css';

type Tipus = ExpertServeiDto['tipus'];

interface FilaExpert {
  clau: number;
  id?: number | null;
  usuariId: number | '';
  tipus: Tipus;
  especialitat: string;
}

/** Edita la llista COMPLETA d'experts del servei: el PUT del backend substitueix tota la llista. */
export function EditaExperts({
  servei,
  experts,
  onTanca,
}: {
  servei: Pick<ServeiDto, 'id' | 'nom'>;
  experts: ExpertServeiDto[];
  onTanca: () => void;
}) {
  const { t } = useTranslation('planificacio');
  const missatgeError = useMissatgeError();
  const usuaris = useUsuaris();
  const desa = useDesaExperts();
  const [seq, setSeq] = useState(experts.length);
  const [files, setFiles] = useState<FilaExpert[]>(() =>
    [...experts]
      .sort((a, b) => (a.tipus === b.tipus ? a.ordre - b.ordre : a.tipus === 'TITULAR' ? -1 : 1))
      .map((e, i) => ({
        clau: i,
        id: e.id,
        usuariId: e.usuariId,
        tipus: e.tipus,
        especialitat: e.especialitat ?? '',
      })),
  );
  const persones = (usuaris.data ?? []).map((u) => ({ id: u.id, nom: nomUsuari(u) }));
  experts.forEach((e) => {
    if (!persones.some((p) => p.id === e.usuariId))
      persones.push({ id: e.usuariId, nom: e.nom ?? `#${e.usuariId}` });
  });

  const triats = files.filter((f) => f.usuariId !== '').map((f) => f.usuariId);
  const duplicat = new Set(triats).size !== triats.length;
  const senseTitular = files.length > 0 && !files.some((f) => f.tipus === 'TITULAR');
  const incomplet = files.some((f) => f.usuariId === '');

  const canvia = (clau: number, canvi: Partial<FilaExpert>) =>
    setFiles(files.map((f) => (f.clau === clau ? { ...f, ...canvi } : f)));
  const afegeix = (tipus: Tipus) => {
    setFiles([...files, { clau: seq, usuariId: '', tipus, especialitat: '' }]);
    setSeq(seq + 1);
  };

  const guarda = () => {
    const ordre: Record<Tipus, number> = { TITULAR: 0, SUBSTITUT: 0 };
    const llista: ExpertServeiDto[] = files
      .filter((f) => f.usuariId !== '')
      .map((f) => {
        ordre[f.tipus] += 1;
        return {
          id: f.id ?? null,
          serveiId: servei.id,
          usuariId: f.usuariId as number,
          tipus: f.tipus,
          ordre: ordre[f.tipus],
          especialitat: f.especialitat.trim() || null,
        };
      });
    desa.mutate({ serveiId: servei.id, experts: llista }, { onSuccess: onTanca });
  };

  return (
    <Modal
      titol={t('experts.edita', { servei: servei.nom })}
      icona={<Icona nom="person-badge" className="icon-20 c-blue" />}
      onTanca={onTanca}
      peu={
        <>
          <button type="button" className="btn btn--s" onClick={onTanca}>
            {t('comu.cancella')}
          </button>
          <button
            type="button"
            className="btn btn--blue btn--s"
            disabled={duplicat || senseTitular || incomplet || desa.isPending}
            onClick={guarda}
          >
            <Icona nom="checkmark" />
            {t('comu.desa')}
          </button>
        </>
      }
    >
      {files.length === 0 ? (
        <div className="empty">
          <Icona nom="person-badge" />
          {t('experts.senseExperts')}
        </div>
      ) : (
        <div className="stack stack--s">
          {files.map((f, i) => (
            <div key={f.clau} className="hstack gap-8" data-expert-fila={i}>
              <Avatar
                nom={f.usuariId === '' ? null : persones.find((p) => p.id === f.usuariId)?.nom}
              />
              <Desplegable<number | ''>
                aria-label={t('experts.persona', { n: i + 1 })}
                valor={f.usuariId}
                onCanvia={(v) => canvia(f.clau, { usuariId: v })}
                opcions={[
                  { valor: '', etiqueta: t('torn.triaPersona') },
                  ...persones.map((p) => ({ valor: p.id, etiqueta: p.nom })),
                ]}
              />
              <span style={{ width: 150, flex: 'none', display: 'flex' }}>
                <Desplegable<Tipus>
                  aria-label={t('experts.tipusFila', { n: i + 1 })}
                  valor={f.tipus}
                  onCanvia={(v) => canvia(f.clau, { tipus: v })}
                  opcions={[
                    { valor: 'TITULAR', etiqueta: t('experts.titular') },
                    { valor: 'SUBSTITUT', etiqueta: t('experts.substitut') },
                  ]}
                />
              </span>
              <input
                className="input"
                style={{ width: 150, flex: 'none' }}
                placeholder={t('experts.especialitat')}
                aria-label={t('experts.especialitat')}
                value={f.especialitat}
                onChange={(e) => canvia(f.clau, { especialitat: e.target.value })}
              />
              <button
                type="button"
                className="btn btn--ghost btn--xs c-red"
                aria-label={t('experts.treu', { n: i + 1 })}
                title={t('experts.treu', { n: i + 1 })}
                onClick={() => setFiles(files.filter((x) => x.clau !== f.clau))}
              >
                <Icona nom="trash" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="hstack gap-8">
        <button type="button" className="btn btn--s" onClick={() => afegeix('TITULAR')}>
          <Icona nom="plus" />
          {t('experts.afegeixTitular')}
        </button>
        <button type="button" className="btn btn--s" onClick={() => afegeix('SUBSTITUT')}>
          <Icona nom="plus" />
          {t('experts.afegeixSubstitut')}
        </button>
      </div>
      {duplicat ? <p className="error-text">{t('experts.igual')}</p> : null}
      {senseTitular ? <p className="error-text">{t('experts.calTitular')}</p> : null}
      {desa.isError ? <p className="error-text">{missatgeError(desa.error)}</p> : null}
    </Modal>
  );
}

function Persones({
  experts,
  nom,
}: {
  experts: ExpertServeiDto[];
  nom: (e: ExpertServeiDto) => string;
}) {
  const { t } = useTranslation('planificacio');
  const [primer, ...resta] = experts;
  if (!primer) return null;
  return (
    <span className="hstack gap-8">
      <Avatar nom={nom(primer)} />
      <span>{nom(primer)}</span>
      {resta.length ? (
        <span className="badge badge--gray" title={resta.map(nom).join(', ')}>
          {t('experts.mes', { count: resta.length })}
        </span>
      ) : null}
    </span>
  );
}

export function W16Experts() {
  const { t } = useTranslation('planificacio');
  const serveis = useServeis();
  const arees = useArees();
  const noms = useNoms();
  const [text, setText] = useState('');
  const [editant, setEditant] = useState<ServeiDto | null>(null);
  const llista = useMemo(() => serveis.data ?? [], [serveis.data]);
  const ids = llista.map((s) => s.id);
  const experts = useExpertsServeis(ids);
  const circuits = useCircuitsServeis(ids);
  const nomExpert = (e: ExpertServeiDto) => e.nom ?? noms(e.usuariId);

  const files = llista
    .map((s, i) => {
      const ex = experts[i]?.data ?? [];
      const circuit = circuits[i]?.data ?? null;
      const perOrdre = (tipus: Tipus) =>
        ex.filter((e) => e.tipus === tipus).sort((a, b) => a.ordre - b.ordre);
      return {
        s,
        area: arees.data?.find((a) => a.id === s.areaId),
        ex,
        titulars: perOrdre('TITULAR'),
        substituts: perOrdre('SUBSTITUT'),
        circuit,
      };
    })
    .filter(
      (r) =>
        !text.trim() ||
        `${r.s.nom} ${r.area?.nom ?? ''} ${r.ex.map(nomExpert).join(' ')}`
          .toLowerCase()
          .includes(text.trim().toLowerCase()),
    );

  return (
    <section className="pantalla" data-screen="W-16">
      <Toolbar titol={t('titols.experts')}>
        <label className="search pla-cerca">
          <Icona nom="magnifier" />
          <input
            type="search"
            placeholder={t('experts.cerca')}
            aria-label={t('experts.cerca')}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </label>
        <Permis recurs="cai.organitzacio" tipus="ALTA">
          <Link className="btn btn--blue btn--s" to="/admin/arees">
            <Icona nom="plus" />
            {t('experts.nouServei')}
          </Link>
        </Permis>
      </Toolbar>
      <div className="content">
        <div className="panel">
          {serveis.isError ? (
            <ErrorCarrega onReintenta={() => void serveis.refetch()} />
          ) : files.length === 0 ? (
            <div className="empty">
              <Icona nom="person-badge" />
              {serveis.isLoading ? t('comu.carregant') : t('experts.buit')}
            </div>
          ) : (
            <table className="taula">
              <thead>
                <tr>
                  <th>{t('experts.servei')}</th>
                  <th>{t('experts.area')}</th>
                  <th>{t('experts.titular')}</th>
                  <th>{t('experts.substitut')}</th>
                  <th>{t('experts.circuit')}</th>
                  <th className="num">{t('experts.confirmacio')}</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {files.map((r) => {
                  const primer = r.circuit?.nivells?.[0];
                  const crisi = r.area?.tipus === 'CRISI';
                  return (
                    <tr key={r.s.id} data-servei={r.s.id}>
                      <td>
                        <div className="hstack gap-8">
                          <Icona
                            nom={crisi ? 'flame' : 'server'}
                            className={crisi ? 'c-red' : 'c-blue'}
                          />
                          <div>
                            <div className="pla-titol">{r.s.nom}</div>
                            {r.titulars[0]?.especialitat ? (
                              <div className="pla-sub">{r.titulars[0].especialitat}</div>
                            ) : null}
                          </div>
                        </div>
                      </td>
                      <td>
                        {r.area ? (
                          <span className="hstack gap-6">
                            {r.area.nom}
                            {crisi ? <BadgeArea tipus={r.area.tipus} /> : null}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>
                        {r.titulars.length ? (
                          <Persones experts={r.titulars} nom={nomExpert} />
                        ) : (
                          <span className="badge badge--critic">{t('experts.senseExpert')}</span>
                        )}
                      </td>
                      <td>
                        {r.substituts.length ? (
                          <Persones experts={r.substituts} nom={nomExpert} />
                        ) : (
                          <span className="dim">{t('experts.senseSubstitut')}</span>
                        )}
                      </td>
                      <td>
                        {r.circuit ? (
                          <span className="hstack gap-6">
                            {r.circuit.nom}
                            {r.circuit.perDefecte ? (
                              <span className="badge badge--gray">{t('experts.perDefecte')}</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="dim">{t('experts.circuitDefecte')}</span>
                        )}
                      </td>
                      <td className="num">
                        {t('experts.minuts', {
                          count: primer ? Math.round(primer.esperaSeg / 60) : 5,
                        })}
                      </td>
                      <td>
                        <Permis recurs="cai.experts" tipus="MODIFICAR">
                          <button
                            type="button"
                            className="btn btn--ghost btn--xs"
                            aria-label={t('experts.edita', { servei: r.s.nom })}
                            title={t('experts.edita', { servei: r.s.nom })}
                            onClick={() => setEditant(r.s)}
                          >
                            <Icona nom="pencil" />
                          </button>
                        </Permis>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {editant ? (
        <EditaExperts
          servei={editant}
          experts={experts[ids.indexOf(editant.id)]?.data ?? []}
          onTanca={() => setEditant(null)}
        />
      ) : null}
    </section>
  );
}
